// RightMenuAddLightBox.jsx
import React, { useEffect, useRef, useState } from "react";
import { Button, HStack } from "@chakra-ui/react";
import { Tooltip } from "../../ui/tooltip";
import { useAppStore } from "../../../context/store";
import { toggleTrafficLightTool } from "../../../utilies/trafficLightTools/Add";
import { createUniqueId, getGeometryTypeGeojson, insertByPriority, matchKeyInText } from "../../../utilies/Utility";
import Type from "../../../utilies/Types";
import { loadGeoJSONToCesium } from "../../../utilies/geojson/LoadGeojson";
import { useMapObjects } from "../../../context/MapObjectsContext";
import HoTrafficLight from "../../../icons/HoTrafficLight";

function TrafficLightTool() {
  // State
  const viewerRef = useAppStore((s) => s.viewerRef);
  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();
  const editMode = useAppStore((s) => s.editMode);
  const defaultGeojsonStyle = useAppStore((s) => s.defaultGeojsonStyle);
  const userData = useAppStore((s) => s.userData);

  const trafficLightMode = useAppStore((s) => s.trafficLightMode);
  const setTrafficLightMode = useAppStore((s) => s.setTrafficLightMode);

  const [isShow, setIsShow] = useState(false);

  // Keep last known datasources for cleanup when store turns mode off externally
  const dsRef = useRef({ light: null, pole: null });

  useEffect(() => {
    const t = { light: null, pole: null };

    for (const key in mapObjects) {
      if (matchKeyInText(mapObjects[key].file_name, "trafficlightbox")) t.light = mapObjects[key].dataSource;
      if (matchKeyInText(mapObjects[key].file_name, "trafficlightpole")) t.pole = mapObjects[key].dataSource;
    }

    dsRef.current = t;
    setIsShow(Boolean(t.light && t.pole));
  }, [mapObjects]);

  const createNewFile = (typeOfLayer) => {
    return new Promise((resolve) => {
      const geojsonData = {
        type: "FeatureCollection",
        crs: {
          type: "name",
          properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" },
        },
        features: [],
      };

      const id = createUniqueId();

      setMapObjects((prevMapObjects) => {
        const newMapObject = {
          id,
          name: "Geojson",
          entities: [],
          type: Type.ASSET.GEOJSON,
          file_name: typeOfLayer + ".geojson",
          file_type: ".geojson",
          inner_entities: [],
          visible: true,
          dataSource: null,
          label_visible: false,
          geojson: geojsonData,
          view_type: "line",
          osm_data: null,
          osmDataSource: null,
          style_stroke_width: null,
          style_fill_color: null,
          style_outline_color: null,
          style_type: null,
          style_pattern: "solid",
          is_center_lane: false,
          auto_select: false,
          show_lanelet: false,
          priority: 0,
          _priorityOrder: typeof performance !== "undefined" ? performance.now() : Date.now(),
          geojson_type: getGeometryTypeGeojson(geojsonData),
        };

        const updatedMapObjects = insertByPriority(prevMapObjects, newMapObject);

        loadGeoJSONToCesium({
          viewer: viewerRef.current,
          geojson: geojsonData,
          options: { ...defaultGeojsonStyle },
          callback: (dataSource, geoJson, billboards, styleFillColor, styleOutsideColor, styleStrokeWidth, styleType) => {
            setTimeout(() => {
              setMapObjects((prevState) => {
                const updatedState = [...prevState];
                const objectIndex = updatedState.findIndex((obj) => obj.id === id);

                if (objectIndex !== -1) {
                  const updatedObject = {
                    ...updatedState[objectIndex],
                    dataSource,
                    billboards,
                    style_stroke_width: styleStrokeWidth,
                    style_fill_color: styleFillColor,
                    style_outline_color: styleOutsideColor,
                    style_type: styleType,
                    loading: false,
                    options: { ...defaultGeojsonStyle },
                  };

                  updatedState[objectIndex] = updatedObject;
                  resolve(updatedObject);
                } else {
                  resolve(null);
                }

                return updatedState;
              });
            }, 500);
          },
          geojsonType: Type.ASSET.GEOJSON,
          layerName: typeOfLayer + ".geojson",
          id,
        });

        return updatedMapObjects;
      });
    });
  };

  const handleToggle = async () => {
    const n = !trafficLightMode;
    setTrafficLightMode(n);

    let t = { light: null, pole: null };

    for (const key in mapObjects) {
      if (matchKeyInText(mapObjects[key].file_name, "trafficlightbox")) t.light = mapObjects[key].dataSource;
      if (matchKeyInText(mapObjects[key].file_name, "trafficlightpole")) t.pole = mapObjects[key].dataSource;
    }

    if (!t.light) {
      const lightObj = await createNewFile("TRAFFIC_LIGHT_BOX");
      if (lightObj && lightObj.dataSource) t.light = lightObj.dataSource;
    }

    if (!t.pole) {
      const poleObj = await createNewFile("TRAFFIC_LIGHT_POLE");
      if (poleObj && poleObj.dataSource) t.pole = poleObj.dataSource;
    }

    dsRef.current = t;

    if (!t.light && !t.pole) {
      setTrafficLightMode(false);
      return;
    }

    toggleTrafficLightTool(
      viewerRef.current,
      function (polygonFeature, pointFeature) {
        const tempMapObjects = [...mapObjects];
        for (const k in tempMapObjects) {
          if (matchKeyInText(tempMapObjects[k].file_name, "trafficlightbox")) tempMapObjects[k].is_edited = true;
          if (matchKeyInText(tempMapObjects[k].file_name, "trafficlightpole")) tempMapObjects[k].is_edited = true;
        }
        setMapObjects(tempMapObjects);
      },
      !n,
      t.light,
      t.pole,
      userData,
      mapObjectsRef,
      setMapObjects,
    );
  };

  // If store turns this mode off externally, actually disable the tool
  useEffect(() => {
    if (trafficLightMode) return;

    const { light, pole } = dsRef.current || {};
    try {
      toggleTrafficLightTool(viewerRef.current, () => {}, true, light, pole, userData, mapObjectsRef, setMapObjects);
    } catch {}
  }, [trafficLightMode, viewerRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const { light, pole } = dsRef.current || {};
      try {
        toggleTrafficLightTool(viewerRef.current, () => {}, true, light, pole, userData, mapObjectsRef, setMapObjects);
      } catch {}
    };
  }, [viewerRef]);

  useEffect(() => {
    if (!trafficLightMode) return;

    const onKeyDown = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      setTrafficLightMode(false);
    };

    window.addEventListener("keydown", onKeyDown, { capture: true });

    return () => {
      window.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [trafficLightMode, setTrafficLightMode]);

  if (!isShow) return;

  return (
    <HStack>
      <Button
        disabled={editMode}
        variant="solid"
        p={0}
        opacity={1}
        bg={trafficLightMode ? "green" : "white"}
        color={trafficLightMode ? "white" : "MenuText"}
        borderWidth={trafficLightMode ? 2 : 1}
        borderColor={trafficLightMode ? "green" : "gray.200"}
        onClick={handleToggle}
        aria-pressed={trafficLightMode}
      >
        <Tooltip content={trafficLightMode ? "Traffic Lights: ON" : "Add Traffic Lights"}>
          <HoTrafficLight />
        </Tooltip>
      </Button>
    </HStack>
  );
}

export default TrafficLightTool;
